import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CurrencyContext = createContext(null);

export const useCurrency = () => useContext(CurrencyContext);

const CURRENCY_CONFIG = {
  GBP: { symbol: '£', locale: 'en-GB', name: 'British Pound' },
  USD: { symbol: '$', locale: 'en-US', name: 'US Dollar' },
  EUR: { symbol: '€', locale: 'de-DE', name: 'Euro' },
  CHF: { symbol: 'CHF', locale: 'de-CH', name: 'Swiss Franc' }
};

export const CurrencyProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [currency, setCurrency] = useState('GBP');
  const [exchangeRates, setExchangeRates] = useState({ GBP: 1, USD: 1.27, EUR: 1.17, CHF: 1.12 });
  const [loading, setLoading] = useState(false);

  // Fetch exchange rates on mount
  useEffect(() => {
    fetchExchangeRates();
  }, []);

  // Update currency when user changes
  useEffect(() => {
    if (user?.currency) {
      setCurrency(user.currency);
    }
  }, [user]);

  const fetchExchangeRates = async () => {
    try {
      const response = await axios.get(`${API}/currency/rates`);
      if (response.data?.rates) {
        setExchangeRates(response.data.rates);
      }
    } catch (error) {
      console.error('Failed to fetch exchange rates:', error);
      // Keep fallback rates
    }
  };

  const updateCurrency = async (newCurrency) => {
    if (!token) {
      // For non-logged-in users, just update local state
      setCurrency(newCurrency);
      localStorage.setItem('assagart_currency', newCurrency);
      return true;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/currency/preference`, { currency: newCurrency });
      setCurrency(newCurrency);
      return true;
    } catch (error) {
      console.error('Failed to update currency:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Convert amount from GBP (base) to selected currency
  const convertFromGBP = useCallback((amountInGBP) => {
    if (currency === 'GBP') return amountInGBP;
    const rate = exchangeRates[currency] || 1;
    return amountInGBP * rate;
  }, [currency, exchangeRates]);

  // Convert amount from selected currency to GBP (for storage)
  const convertToGBP = useCallback((amount) => {
    if (currency === 'GBP') return amount;
    const rate = exchangeRates[currency] || 1;
    return amount / rate;
  }, [currency, exchangeRates]);

  // Format currency with proper symbol and locale
  const formatCurrency = useCallback((value, options = {}) => {
    if (value === null || value === undefined) {
      const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.GBP;
      return `${config.symbol}0`;
    }

    const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.GBP;
    const convertedValue = options.skipConversion ? value : convertFromGBP(value);

    return new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: options.decimals ?? 0,
      maximumFractionDigits: options.decimals ?? 0,
    }).format(convertedValue);
  }, [currency, convertFromGBP]);

  // Get currency symbol
  const getCurrencySymbol = useCallback(() => {
    return CURRENCY_CONFIG[currency]?.symbol || '£';
  }, [currency]);

  return (
    <CurrencyContext.Provider value={{
      currency,
      currencies: Object.keys(CURRENCY_CONFIG),
      currencyConfig: CURRENCY_CONFIG,
      exchangeRates,
      loading,
      updateCurrency,
      formatCurrency,
      convertFromGBP,
      convertToGBP,
      getCurrencySymbol,
      refreshRates: fetchExchangeRates
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};
